/**
 * Lift — does a channel, campaign or ad convert better as the FIRST thing
 * someone saw, or as a LATER touch that brought them back?
 *
 * Built on the attribution engine (backend services/attribution/lift): the same
 * journeys as the Channels report, the window's leads plus its new-sale
 * customers, one person per email, each buyer's journey cut at their payment.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { format, subDays } from 'date-fns'
import { dailyReportAPI } from '../services/api'
import { DateRangePicker } from '../components/DateRangePicker'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, ChevronRight, TrendingUp } from 'lucide-react'
import { sortLiftRows, liftTone, drillFrom, pct, times, count, dollars } from '../lib/lift'

const LEVEL_LABELS = { channel: 'Channel', campaign: 'Campaign', content: 'Ad / content' }

function defaultRange() {
  const to = new Date()
  return { from: subDays(to, 29), to }
}

const TONE_STYLES = {
  up: { backgroundColor: '#16A34A', color: '#FFFFFF' },
  down: { backgroundColor: '#DC2626', color: '#FFFFFF' },
  flat: { backgroundColor: 'transparent', color: 'inherit', border: '1px solid currentColor', opacity: 0.6 },
}

function Lift({ side }) {
  const tone = liftTone(side)
  if (!tone) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium tabular-nums" style={TONE_STYLES[tone]}
      title={side.significant ? 'Significant at 95%' : 'Not significant: the interval includes the average'}>
      {times(side.lift)}
    </span>
  )
}

function Rate({ side }) {
  if (!side?.people) return <span className="text-muted-foreground">—</span>
  return (
    <div className="tabular-nums leading-tight">
      <div className="font-medium text-foreground">{pct(side.cvr)}</div>
      <div className="text-[11px] text-muted-foreground">{pct(side.ci_low)}–{pct(side.ci_high)}</div>
    </div>
  )
}

const COLUMNS = [
  { path: 'initial.people', label: 'People', side: 'initial' },
  { path: 'initial.buyers', label: 'Buyers', side: 'initial' },
  { path: 'initial.cvr', label: 'CVR', side: 'initial' },
  { path: 'initial.lift', label: 'Lift', side: 'initial' },
  { path: 'later.people', label: 'People', side: 'later' },
  { path: 'later.buyers', label: 'Buyers', side: 'later' },
  { path: 'later.cvr', label: 'CVR', side: 'later' },
  { path: 'later.lift', label: 'Lift', side: 'later' },
  { path: 'later_vs_initial', label: 'Later ÷ initial' },
  { path: 'spend', label: 'Spend' },
  { path: 'spend_per_buyer_reached', label: 'Spend / buyer reached' },
]

function cell(row, column) {
  const side = column.side ? row[column.side] : null
  switch (column.label) {
    case 'People': return count(side.people)
    case 'Buyers': return count(side.buyers)
    case 'CVR': return <Rate side={side} />
    case 'Lift': return <Lift side={side} />
    case 'Later ÷ initial': return times(row.later_vs_initial)
    case 'Spend': return dollars(row.spend)
    default: return dollars(row.spend_per_buyer_reached)
  }
}

function LiftTable({ rows, groupBy, onDrill }) {
  const [sort, setSort] = useState({ path: 'any.people', direction: 'desc' })
  const sorted = useMemo(() => sortLiftRows(rows, sort.path, sort.direction), [rows, sort])
  const toggle = path => setSort(prev => ({ path, direction: prev.path === path && prev.direction === 'desc' ? 'asc' : 'desc' }))
  const arrow = path => (sort.path === path ? (sort.direction === 'desc' ? ' ↓' : ' ↑') : '')
  const canDrill = groupBy !== 'content'

  if (!rows.length) return <div className="py-8 text-center text-muted-foreground">No touches in this window.</div>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground">
            <th />
            <th colSpan={4} className="border-b border-border px-2 pb-1 text-center font-medium">As the first touch</th>
            <th colSpan={4} className="border-b border-border px-2 pb-1 text-center font-medium">As a later touch</th>
            <th colSpan={3} />
          </tr>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="cursor-pointer whitespace-nowrap px-2 py-2 text-left font-medium" onClick={() => toggle('label')}>
              {LEVEL_LABELS[groupBy]}{arrow('label')}
            </th>
            {COLUMNS.map(column => (
              <th key={column.path} className="cursor-pointer whitespace-nowrap px-2 py-2 text-right font-medium" onClick={() => toggle(column.path)}>
                {column.label}{arrow(column.path)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => (
            <tr key={row.key} className="border-b border-border/50 hover:bg-muted/40">
              <td className="max-w-[320px] px-2 py-2">
                {canDrill ? (
                  <button className="flex items-center gap-1 text-left font-medium text-foreground hover:underline" onClick={() => onDrill(row)}>
                    <span className="truncate">{row.label || <em className="text-muted-foreground">No campaign recorded</em>}</span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                ) : (
                  <span className="block truncate font-medium text-foreground">{row.label || <em className="text-muted-foreground">No ad recorded</em>}</span>
                )}
                {groupBy !== 'channel' && row.matched === false && (
                  <span className="text-[11px] text-muted-foreground">from UTMs (no ad spend match)</span>
                )}
              </td>
              {COLUMNS.map(column => (
                <td key={column.path} className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{cell(row, column)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Stat({ label, value, note }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
        {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
      </CardContent>
    </Card>
  )
}

export default function LiftPage() {
  const [dateRange, setDateRange] = useState(defaultRange)
  // The drill path: channel → campaign → ad.
  const [trail, setTrail] = useState([{ groupBy: 'channel', channel: null, campaign: null, label: 'All channels' }])
  const level = trail[trail.length - 1]
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return
    setLoading(true)
    setError(null)
    try {
      const res = await dailyReportAPI.getLift({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        groupBy: level.groupBy,
        ...(level.channel ? { channel: level.channel } : {}),
        ...(level.campaign ? { campaign: level.campaign } : {}),
      })
      setData(res.data?.data || null)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load lift')
    } finally {
      setLoading(false)
    }
  }, [dateRange, level])

  useEffect(() => { load() }, [load])

  const drill = row => {
    const next = drillFrom(level.groupBy, row)
    if (next) setTrail(prev => [...prev, next])
  }

  const summary = data?.summary

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Lift</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Does a channel convert better as someone's first touch, or as a later one that brought them back?
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {data && !data.leads_available && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
          Leads could not be read for this clinic, so everyone counted is a buyer and every rate reads 100%.
        </div>
      )}
      {data?.bigquery_touches && data.bigquery_touches.checked === false && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
          BigQuery's paid clicks could not be merged in; journeys are core's recorded visits only.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="People" value={count(summary?.people)} note="Leads created in the range, plus anyone with a new sale in it" />
        <Stat label="Buyers" value={count(summary?.buyers)}
          note={summary ? `${count(summary.new_sales)} new sales on the Daily Report` : null} />
        <Stat label="Average CVR" value={pct(summary?.cvr)} note="Buyers ÷ people; every lift is against this" />
        <Stat label="No recorded touch" value={count(summary?.no_journey?.people)}
          note={summary ? `${count(summary.no_journey.buyers)} of them bought; counted in the average, on no row` : null} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-1 text-base">
            <TrendingUp className="mr-1 h-5 w-5" />
            {trail.map((step, index) => (
              <span key={index} className="flex items-center gap-1">
                {index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                {index < trail.length - 1 ? (
                  <button className="text-primary hover:underline" onClick={() => setTrail(trail.slice(0, index + 1))}>{step.label}</button>
                ) : (
                  <span>{step.label || 'No campaign recorded'}</span>
                )}
              </span>
            ))}
          </CardTitle>
          <CardDescription>
            Each person counts once per row and side, however often they came back. Click a row to break it down.
            Lift is the row's CVR over the average; coloured only when significant at 95%.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className={loading ? 'opacity-50' : ''}>
              <LiftTable rows={data?.rows || []} groupBy={level.groupBy} onDrill={drill} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">How to read this</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p><strong>First touch</strong> is the first thing a person ever came through, not the first inside the date range. <strong>Later</strong> is any touch after that and before they paid. A visit after paying never counts.</p>
          <p><strong>People</strong> are those who reached the intake: leads created in the range, plus customers with a new sale in it, one per email. Renewals, quick refunds and store-credit charges are not sales, as on the Daily Report.</p>
          <p><strong>Journeys</strong> are the attribution engine's: core's recorded visits plus paid clicks BigQuery saw and core missed. Customer.io's resume-link fetch is not a touch, and neither is the price list a sale closed on.</p>
          <p><strong>Spend / buyer reached</strong> divides a row's ad spend by every buyer it touched at all. A buyer two rows reached counts in both, so these do not add up to a CAC.</p>
          <p>Leads from the last few days may still buy. This is correlation: people a retargeting ad brings back were already more likely to buy. Only a holdout measures what a channel causes.</p>
        </CardContent>
      </Card>
    </div>
  )
}
